import { useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useQuotesList,
  type QuoteListFilters,
  type QuoteListItem,
  type QuoteStatus,
} from '@/hooks/useQuotes';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FilterChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { Skeleton } from '@/components/ui/Skeleton';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { FINANCE_NUM, formatFinanceAmount, formatFinanceDate } from '@/pages/economy/financeFormat';

const STATUSES: Array<QuoteStatus | 'all'> = [
  'all',
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
  'withdrawn',
];

function quoteStatusTone(status: QuoteStatus): StatusTone {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'sent':
      return 'upcoming';
    case 'accepted':
      return 'completed';
    case 'rejected':
      return 'red';
    case 'expired':
      return 'draft';
    case 'withdrawn':
      return 'cancelled';
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

export function QuoteStatusBadge({ status }: { status: QuoteListItem['status'] }) {
  const { t } = useTranslation();
  return <StatusChip tone={quoteStatusTone(status)}>{t(`quotes.status_${status}`)}</StatusChip>;
}

export function Quotes({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filters = useMemo<QuoteListFilters>(
    () => ({ status: statusFilter, search: searchTerm.trim() || undefined }),
    [statusFilter, searchTerm],
  );

  const { data, isLoading, error, refetch } = useQuotesList(activeClinicId, filters);
  const quotes = data ?? [];

  const newQuoteButton = (
    <Button onClick={() => navigate('/quotes/new')} disabled={!activeClinicId}>
      {t('quotes.new_quote')}
    </Button>
  );

  const columns: TableColumn<QuoteListItem>[] = [
    {
      id: 'number',
      header: t('quotes.col_number'),
      cell: (q) => <QuoteNumberCell quote={q} />,
    },
    {
      id: 'patient',
      header: t('quotes.col_patient'),
      cell: (q) => (
        <span className="font-medium">
          {q.patient_first_name} {q.patient_last_name}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('quotes.col_status'),
      cell: (q) => <QuoteStatusBadge status={q.status} />,
    },
    {
      id: 'total',
      header: t('quotes.col_total'),
      numeric: true,
      cell: (q) => (
        <span className={cn(FINANCE_NUM, 'font-semibold')}>
          {formatFinanceAmount(q.total_cents, i18n.language)}
        </span>
      ),
    },
    {
      id: 'valid',
      header: t('quotes.col_valid_until'),
      cell: (q) => (
        <span className={FINANCE_NUM}>
          {formatFinanceDate(q.valid_until, i18n.language) ?? '—'}
        </span>
      ),
    },
    {
      id: 'sent',
      header: t('quotes.col_sent_at'),
      cell: (q) => (
        <span className={FINANCE_NUM}>{formatFinanceDate(q.sent_at, i18n.language) ?? '—'}</span>
      ),
    },
  ];

  const headingRow = (
    <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
      {embedded ? null : (
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('quotes.title')}</h1>
        </div>
      )}
      <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('quotes.search_placeholder')}
          aria-label={t('quotes.search_placeholder')}
        />
      </div>
      <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
        <div className="flex flex-wrap items-center gap-s2" role="group" aria-label={t('quotes.col_status')}>
          {STATUSES.map((s) => (
            <FilterChip key={s} pressed={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {t(`quotes.status_${s}`)}
            </FilterChip>
          ))}
        </div>
        {newQuoteButton}
      </div>
    </div>
  );

  const listBody = isLoading ? (
    <Card>
      <div className="flex flex-col gap-s3" aria-busy>
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    </Card>
  ) : error ? (
    <Banner
      tone="error"
      action={
        <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
          {t('app.retry')}
        </Button>
      }
    >
      {t('quotes.load_error')}
    </Banner>
  ) : quotes.length === 0 ? (
    <Card>
      <EmptyState
        icon={<FileText className="h-7 w-7" />}
        title={
          statusFilter !== 'all'
            ? t('quotes.empty_filter_title')
            : searchTerm
              ? t('quotes.empty_search_title')
              : t('quotes.empty_title')
        }
        action={searchTerm ? undefined : newQuoteButton}
      />
    </Card>
  ) : (
    <Card className="p-0 nav:p-0">
      <ResponsiveTable
        columns={columns}
        rows={quotes}
        getRowKey={(q) => q.id}
        getRowHref={(q) => `/quotes/${q.id}`}
        getRowLabel={(q) =>
          `${q.patient_first_name ?? ''} ${q.patient_last_name ?? ''}`.trim() ||
          q.quote_number ||
          t('quotes.no_number')
        }
        empty={null}
        mobileCard={(q) => (
          <div className="w-full text-left">
            <QuoteNumberCell quote={q} />
            <div className={cn(TYPE.small, FINANCE_NUM, 'mt-s1 text-ds-muted')}>
              {q.patient_first_name} {q.patient_last_name} ·{' '}
              {formatFinanceAmount(q.total_cents, i18n.language)}
            </div>
            <div className="mt-s2">
              <QuoteStatusBadge status={q.status} />
            </div>
          </div>
        )}
      />
    </Card>
  );

  const content = (
    <>
      {headingRow}
      <div className="mt-s5">{listBody}</div>
    </>
  );

  if (embedded) return content;
  return <PageLayout>{content}</PageLayout>;
}

function QuoteNumberCell({ quote }: { quote: QuoteListItem }) {
  const { t } = useTranslation();
  const href = `/quotes/${quote.id}`;
  return (
    <Link
      to={href}
      tabIndex={-1}
      onClick={stopRowInteractive}
      className={cn(TYPE.body, FINANCE_NUM, 'block font-medium text-ds-text')}
    >
      {quote.quote_number ?? t('quotes.no_number')}
    </Link>
  );
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}
